import { Menu as MuiMenu } from '@mui/material';
import type { ElementType } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDeleteLayout } from '~/api/layouts';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import {
  MoreDeleteAction,
  mergeGridActionProps,
  type GridActionCellProps,
} from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { LayoutList__Layout as Layout } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  layout: Layout;
  Icon?: ElementType;
}

export const Menu = ({ layout, ...cellProps }: MenuProps) => {
  const { t } = useTranslation();
  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<Layout>();
  const layoutActionProps = mergeGridActionProps(moreTableActionProps(layout), cellProps);
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteLayout] = useDeleteLayout();

  return (
    <>
      <ActionButton {...layoutActionProps} />
      <MuiMenu {...moreMenuProps}>
        <MoreDeleteAction
          tooltip={layout.canDestroy.reasons?.fullMessages[0]}
          disabled={!layout.canDestroy.value}
          {...moreActionProps}
          onClick={async () => {
            assert(moreMenuProps.context !== undefined);
            if (!(await confirmDelete())) return;
            await deleteLayout({
              variables: { input: { layoutId: moreMenuProps.context.id } },
            });
            moreMenuProps.onClose();
          }}
        />
      </MuiMenu>

      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={
          <Trans i18nKey="pages.layouts.delete_dialog.prompt" components={{ b: <strong /> }} />
        }
        title={t('pages.layouts.delete_dialog.title')}
      />
    </>
  );
};
